import React, { FC, ReactNode, useState } from 'react';
import classnames from 'classnames';
import { uniqBy } from 'lodash';
import { Container } from '@src/components/Container';
import { Avatar } from '@src/components/Avatar';
import { getNameInitials } from '@src/util/getNameInitials';
import { relativeTime } from '@src/util/relativeTime';
import { Tooltip } from '@src/components/Tooltip';
import { MessageThreadStateEnum, UserApplicationStateEnum, UserProfileLinkTypeEnum } from '@src/graphql/generated';
import { AvatarSize } from '@src/components/Avatar/enums/avatarSize.enum';
import { isVerifiedUserApplicantType } from './typeGuards/isVerifiedUserApplicantType';
import { isOpeningApplicantType } from './typeGuards/isOpeningApplicantType';
import { ApplicantCardActionTypes } from './enums/applicantCardActionTypes.enum';
import { ClosedActions } from './ClosedActions';
import { Actions } from './Actions';
import { PendingActions } from './PendingActions';
import { OpeningApplicantCardProps } from './props';
import './styles.scss';

export * from './Skeleton';
export * from './Placeholder';

const SKILLS_COLLAPSED_DISPLAY_AMOUNT = 5;

const LINK_TYPE_TO_LABEL = {
  [UserProfileLinkTypeEnum.BEHANCE]: 'Behance',
  [UserProfileLinkTypeEnum.DRIBBBLE]: 'Dribbble',
  [UserProfileLinkTypeEnum.GITHUB]: 'GitHub',
  [UserProfileLinkTypeEnum.GITLAB]: 'GitLab',
  [UserProfileLinkTypeEnum.BITBUCKET]: 'Bitbucket',
  [UserProfileLinkTypeEnum.LINKEDIN]: 'LinkedIn',
  [UserProfileLinkTypeEnum.PORTFOLIO]: 'Portfolio',
  [UserProfileLinkTypeEnum.OTHER]: 'Other',
};

export const OpeningApplicantCard: FC<OpeningApplicantCardProps> = ({
  wrapperClassName,
  userData,
  isUnread,
  onCardClick,
  onOpenChat,
  onAccept,
  onDecline,
  actionType = ApplicantCardActionTypes.OPEN_CHAT,
}) => {
  const [isSkillsCollapsed, setIsSkillsCollapsed] = useState(true);

  const user = userData.user;
  const userProfile = user.profile;
  const avatarUrl = user.avatar?.fullUrl;
  const avatarColor = user.avatarColor;
  const position = user.position;
  const links = userProfile.links;
  const skills = userProfile.skills;
  const seniorities = userProfile.seniorities;
  const speciality = userProfile.speciality.name;
  const userMessageThreadState = userData.application.messageThread?.userState;

  const positionExpectations = isVerifiedUserApplicantType(userData)
    ? userData.userVerification.positionExpectations
    : undefined;

  const nameInitials = getNameInitials(user.name);

  const locationNames = [
    userData.user.profile.location.shortName,
    ...(userData.user.profile.isRemote ? ['Remote'] : []),
    ...(userData.user.profile.isRelocation ? ['Relocation'] : []),
  ];

  const originalSalaryValue = userProfile.originalSalary.value || undefined;
  const originalSalaryCurrency = userProfile.originalSalary.currency.symbol || undefined;

  const expectedSalary =
    originalSalaryValue && originalSalaryCurrency ? `${originalSalaryValue} ${originalSalaryCurrency}` : undefined;

  const uniqueExperience = uniqBy(userData.user.profile.workHistory, 'company.id');

  const workHistory = uniqueExperience.filter(({ company }) => !!company).slice(0, 3);

  const skillsData = isSkillsCollapsed ? skills.slice(0, SKILLS_COLLAPSED_DISPLAY_AMOUNT) : skills;

  const getTimestamp = (): string | undefined => {
    if (userData.application.messageThread?.isInitiatedByMeetFrank) {
      return 'Invited by Virtual Recruiter';
    }

    if (isVerifiedUserApplicantType(userData) && userData.application?.userState?.createdAt) {
      if (userData.application?.userState.state === UserApplicationStateEnum.REJECTED) {
        return `Declined invite ${relativeTime(userData.application.userState.createdAt, false)}`;
      }

      return `Accepted invite ${relativeTime(userData.application.userState.createdAt, false)}`;
    }

    if (isOpeningApplicantType(userData)) {
      return `Applied directly ${relativeTime(userData.application.createdAt, false)}`;
    }

    return undefined;
  };

  const renderActions = (): ReactNode => {
    if (userMessageThreadState === MessageThreadStateEnum.CLOSED) {
      return <ClosedActions isUnread={isUnread} onOpenChat={onOpenChat} onArchive={onDecline} />;
    }

    if (actionType === ApplicantCardActionTypes.OPEN_CHAT && onOpenChat) {
      return <Actions isUnread={isUnread} onOpenChat={onOpenChat} />;
    }

    if (actionType === ApplicantCardActionTypes.PENDING && onAccept && onDecline) {
      return <PendingActions isUnread={isUnread} onAccept={onAccept} onDecline={onDecline} onOpenChat={onOpenChat} />;
    }

    return null;
  };

  return (
    <Container
      className={classnames('opening-applicant-card', isUnread && 'opening-applicant-card--unread', wrapperClassName)}
      onClick={onCardClick}
    >
      <div className="opening-applicant-card__left">
        <Avatar
          avatarColor={avatarColor || undefined}
          initials={nameInitials}
          size={AvatarSize.X_LARGE}
          source={avatarUrl}
        />
      </div>

      <div className="opening-applicant-card__right">
        <div className="opening-applicant-card__header">
          <div className="opening-applicant-card__talent-main-info">
            <div>
              <h4>
                {user.name} • {position}
              </h4>
            </div>

            <p>
              {locationNames.join(', ')} • {expectedSalary}
            </p>
          </div>

          <span className="opening-applicant-card__time">{getTimestamp()}</span>
        </div>

        <div className="opening-applicant-card__content">
          {isVerifiedUserApplicantType(userData) && positionExpectations && false && (
            <div className="opening-applicant-card__talent-looking-for">
              <h4>I'm looking for</h4>

              <p>{positionExpectations}</p>
            </div>
          )}

          {!positionExpectations && (
            <>
              {!!speciality && (
                <div className="opening-applicant-card__speciality">
                  <h4>Speciality</h4>

                  <p>{speciality}</p>
                </div>
              )}

              {!!seniorities.length && (
                <div className="opening-applicant-card__seniority">
                  <h4>Seniority</h4>

                  <p>{seniorities.map(({ name }) => name).join(', ')}</p>
                </div>
              )}
            </>
          )}

          {isOpeningApplicantType(userData) && !!skills.length && (
            <div className="opening-applicant-card__skills-wrapper">
              <h4>Skills</h4>

              <div className="opening-applicant-card__skills">
                {skillsData.map(skill => (
                  <div className="opening-applicant-card__skill fade-in" key={skill.id}>
                    {skill.name}
                  </div>
                ))}

                {isSkillsCollapsed && skills.length - SKILLS_COLLAPSED_DISPLAY_AMOUNT > 0 && (
                  <div
                    className="opening-applicant-card__skill-expand"
                    onClick={event => {
                      event.stopPropagation();

                      setIsSkillsCollapsed(!isSkillsCollapsed);
                    }}
                  >
                    +{skills.length - SKILLS_COLLAPSED_DISPLAY_AMOUNT} more
                  </div>
                )}
              </div>
            </div>
          )}

          {!!workHistory.length && (
            <div className="opening-applicant-card__talent-prev-experience">
              <h4>Previous experience</h4>

              <div className="opening-applicant-card__companies">
                {workHistory.map(({ company }) => (
                  <Tooltip
                    key={company?.id}
                    overlay={<>{company?.profile.name}</>}
                    overlayStyle={{
                      maxWidth: '25rem',
                      textAlign: 'center',
                    }}
                    placement="top"
                  >
                    <div className="opening-applicant-card__company-logo-wrapper">
                      <Avatar
                        className="opening-applicant-card__company-logo"
                        key={company?.id}
                        size={AvatarSize.MEDIUM}
                        source={company?.profile.logo?.fullUrl}
                      />
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}

          {!!links.length && (
            <div className="opening-applicant-card__links">
              <h4>Links</h4>

              <div className="opening-applicant-card__links-list">
                {links.map(link => {
                  return (
                    <a
                      className="opening-applicant-card__link"
                      href={link.value}
                      key={link.type + link.value}
                      onClick={event => event.stopPropagation()}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {LINK_TYPE_TO_LABEL[link.type]}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="opening-applicant-card__actions">{renderActions()}</div>
      </div>
    </Container>
  );
};
